import {
    isServer,
    QueryCache,
    QueryClient,
    type FetchQueryOptions,
    type QueryKey,
} from "@tanstack/react-query";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

const createQueryClient = () =>
    new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 60 * 1000 * 5,
                retry: 3,
                retryOnMount: false,
                refetchOnWindowFocus: false,
            },
        },
        queryCache: new QueryCache({
            onError: (error, query) => {
                if (!query.meta) {
                    Logger.error(ServiceType.WEB, "Query error without logging metadata", {
                        tag: LogTag.ERROR,
                        error,
                    });
                    return;
                }
                Logger.error(query.meta.serviceType as ServiceType, query.meta.message as string, {
                    tag: query.meta.tag as LogTag,
                    ...(query.meta.payload as Record<string, any>),
                    error,
                });
            },
        }),
    });

let browserQueryClient: QueryClient | undefined;

export function getQueryClient(): QueryClient {
    if (isServer) {
        return createQueryClient();
    }

    if (!browserQueryClient) {
        browserQueryClient = createQueryClient();
    }

    return browserQueryClient;
}

export async function prefetchQuery<TData>(
    queryClient: QueryClient,
    queryKey: QueryKey,
    queryFunction: () => Promise<TData>,
    options?: FetchQueryOptions<TData>
): Promise<QueryClient> {
    await queryClient.prefetchQuery({
        queryKey,
        queryFn: queryFunction,
        ...options,
    });

    return queryClient;
}
